import type {TMetricsWindow} from "@/api/customerSuccessApi";
import BuildingIcon from "@/assets/colorable/building.svg?react";
import DownUpArrowIcon from "@/assets/colorable/down_up_arrow.svg?react";
import Button from "@/components/Common/Button";
import SegmentedToggle from "@/components/Common/forms/SegmentedToggle";
import SortButton from "@/components/Common/SortButton";
import {OPTION_CHIP_CLASS} from "@/components/CustomerSuccess/customerSuccessStyles";
import FilterButton from "@/components/Influencer/influencerNetwork/components/FilterButton";
import {packages} from "@/hooks/usePackages";
import type {TPackageName} from "@/store/types";
import {useNavigate, useSearch} from "@tanstack/react-router";
import dayjs from "dayjs";
import {Fragment, useState, type FC, type PropsWithChildren, type SetStateAction} from "react";
import {useTranslation} from "react-i18next";
import {useDebounce} from "react-use";
import {z} from "zod";

// smallest to largest tier — the same order the Plan column sorts in
const PLAN_TIERS = ["STARTER", "ESSENTIALS", "ADVANCED", "PROFESSIONAL", "EXPERT", "ENTERPRISE"] as const;
const BILLING_PERIODS = ["MONTHLY", "QUARTERLY", "HALF_YEARLY", "YEARLY"] as const;
// who set up the latest subscription: bought through Stripe, or assigned by a Refluenced admin
const BILLING_SOURCES = ["stripe", "admin_assigned"] as const;
const RENEWAL_OPTIONS = ["30", "60", "90", "cancellation_pending", "expired"] as const;
const RENEWAL_MONTH_REGEX = /^\d{4}-(0[1-9]|1[0-2])$/;
export const CUSTOMER_SUCCESS_SEARCH_SCHEMA = z.object({
  billing_source: z.enum(BILLING_SOURCES).optional().catch(undefined),
  cancellation_pending: z.boolean().optional(),
  expired: z.boolean().optional(),
  ordering: z
    .enum([
      "agency_name",
      "-agency_name",
      "plan",
      "-plan",
      "renewal_date",
      "-renewal_date",
      "lifetime_cash_cpm",
      "-lifetime_cash_cpm",
      "lifetime_cash_product_cpm",
      "-lifetime_cash_product_cpm",
      "lifetime_total_cpm",
      "-lifetime_total_cpm",
      "period_cash_cpm",
      "-period_cash_cpm",
      "period_cash_product_cpm",
      "-period_cash_product_cpm",
      "period_total_cpm",
      "-period_total_cpm",
      "tenure_started_at",
      "-tenure_started_at",
    ])
    .optional()
    .catch(undefined),
  package_name: z.enum(PLAN_TIERS).optional(),
  package_type: z.enum(BILLING_PERIODS).optional(),
  renewal_month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional(),
  renewing_within_days: z.union([z.literal(30), z.literal(60), z.literal(90)]).optional(),
  search: z.string().optional(),
  three_plus_twelve: z.boolean().optional(),
  window: z.enum(["lifetime", "period"]).catch("lifetime").default("lifetime"),
});

export type TCustomerSuccessSearch = z.infer<typeof CUSTOMER_SUCCESS_SEARCH_SCHEMA>;

export type TCustomerSuccessOrdering = NonNullable<TCustomerSuccessSearch["ordering"]>;

type TCpmColumn = "cash_cpm" | "cash_product_cpm" | "total_cpm";

// The CPM orderings carry the metrics window ("lifetime_cash_cpm"); the sort menu offers each CPM once and
// the selected window decides which of the two server orderings it sends.
type TSortOption =
  | Exclude<TCustomerSuccessOrdering, `${"" | "-"}${TMetricsWindow}_${TCpmColumn}`>
  | `${"" | "-"}${TCpmColumn}`;

const CPM_OPTION_REGEX = /^(-?)(cash_cpm|cash_product_cpm|total_cpm)$/;
export const WINDOW_PREFIX_REGEX = /^(-?)(?:lifetime|period)_(?=(?:cash_cpm|cash_product_cpm|total_cpm)$)/;

type TBillingOption = (typeof BILLING_PERIODS)[number] | "three_plus_twelve";
// a fixed option or a "YYYY-MM" calendar month
type TRenewalOption = string;

const CustomerSuccessFilters: FC<PropsWithChildren> = ({children}) => {
  const {t} = useTranslation(["customer_success", "common"]);
  const navigate = useNavigate();
  const search = useSearch({
    from: "/_authenticated/_admin/admins/customer-success",
    select: search =>
      CUSTOMER_SUCCESS_SEARCH_SCHEMA.parse(search.view === "churn" ? {...search, ordering: undefined} : search),
  });
  const [searchText, setSearchText] = useState(search.search ?? "");

  const update = (patch: Partial<TCustomerSuccessSearch>) =>
    navigate({to: ".", search: prev => ({...prev, ...patch}), replace: true, resetScroll: false});

  useDebounce(
    () => {
      if ((search.search ?? "") !== searchText.trim()) update({search: searchText.trim() || undefined});
    },
    500,
    [searchText],
  );

  // billing period and the 3+12 contract flag are exposed as one single-choice "Billing" filter
  const billing: TBillingOption | null = search.three_plus_twelve ? "three_plus_twelve" : (search.package_type ?? null);
  const setBilling = (value: TBillingOption | null) =>
    update({
      package_type: value && value !== "three_plus_twelve" ? value : undefined,
      three_plus_twelve: value === "three_plus_twelve" || undefined,
    });

  // the renewal-related backend filters are exposed as one single-choice "Renewal" filter:
  // rolling windows, the previous 3 and next 12 calendar months, cancellation pending, expired
  const renewal: TRenewalOption | null = search.cancellation_pending
    ? "cancellation_pending"
    : search.expired
      ? "expired"
      : search.renewing_within_days
        ? String(search.renewing_within_days)
        : (search.renewal_month ?? null);
  const setRenewal = (value: TRenewalOption | null) =>
    update({
      cancellation_pending: value === "cancellation_pending" || undefined,
      expired: value === "expired" || undefined,
      renewal_month: value && RENEWAL_MONTH_REGEX.test(value) ? value : undefined,
      renewing_within_days: value === "30" ? 30 : value === "60" ? 60 : value === "90" ? 90 : undefined,
    });
  // past months list every customer whose period ended then (renewed or not) so sales can cross-check
  const renewalMonths = Array.from({length: 15}, (_, index) =>
    dayjs().subtract(3, "month").add(index, "month").format("YYYY-MM"),
  );

  const planLabel = (tier: TPackageName) => t(`common:${packages[tier].translationKey as "packageAdvanced"}`);
  const billingLabels: Record<TBillingOption, string> = {
    HALF_YEARLY: t("package_type_HALF_YEARLY"),
    MONTHLY: t("package_type_MONTHLY"),
    QUARTERLY: t("package_type_QUARTERLY"),
    three_plus_twelve: t("filter_three_plus_twelve"),
    YEARLY: t("package_type_YEARLY"),
  };
  const billingSourceLabels: Record<(typeof BILLING_SOURCES)[number], string> = {
    admin_assigned: t("filter_source_admin_assigned"),
    stripe: t("filter_source_stripe"),
  };
  const renewalLabel = (option: TRenewalOption) => {
    if (RENEWAL_MONTH_REGEX.test(option)) return dayjs(`${option}-01`).format("MMM YYYY");
    const labels: Record<(typeof RENEWAL_OPTIONS)[number], string> = {
      "30": t("filter_30d"),
      "60": t("filter_60d"),
      "90": t("filter_90d"),
      cancellation_pending: t("filter_cancellation_pending"),
      expired: t("filter_expired"),
    };
    return labels[option as (typeof RENEWAL_OPTIONS)[number]];
  };
  // insertion order is the menu order: the table's column order
  const sortLabels: Record<TSortOption, string> = {
    agency_name: t("sort_agency_name_asc"),
    "-agency_name": t("sort_agency_name_desc"),
    plan: t("sort_plan_asc"),
    "-plan": t("sort_plan_desc"),
    renewal_date: t("sort_renewal_date_asc"),
    "-renewal_date": t("sort_renewal_date_desc"),
    cash_cpm: t("sort_cpm_asc", {label: t("col_cash_cpm")}),
    "-cash_cpm": t("sort_cpm_desc", {label: t("col_cash_cpm")}),
    cash_product_cpm: t("sort_cpm_asc", {label: t("col_cash_product_cpm")}),
    "-cash_product_cpm": t("sort_cpm_desc", {label: t("col_cash_product_cpm")}),
    total_cpm: t("sort_cpm_asc", {label: t("col_total_cpm")}),
    "-total_cpm": t("sort_cpm_desc", {label: t("col_total_cpm")}),
    tenure_started_at: t("sort_tenure_started_at_asc"),
    "-tenure_started_at": t("sort_tenure_started_at_desc"),
  };
  const sortOption = (search.ordering?.replace(WINDOW_PREFIX_REGEX, "$1") as TSortOption | undefined) ?? null;
  // a CPM sort follows the window toggle: the same CPM and direction in the newly selected window
  const setWindow = (metricsWindow: TMetricsWindow) =>
    update({
      window: metricsWindow,
      ...(search.ordering && {
        ordering: search.ordering.replace(WINDOW_PREFIX_REGEX, `$1${metricsWindow}_`) as TCustomerSuccessOrdering,
      }),
    });
  const hasActiveFilters =
    !!search.search ||
    !!search.package_name ||
    billing !== null ||
    !!search.billing_source ||
    renewal !== null ||
    !!search.ordering;

  const renderOptions = <T extends string>(
    options: {label: string; value: T | null}[],
    selected: T | null,
    onSelect: (value: T | null) => void,
    dataTestId?: string,
  ) => (
    <div className="flex max-w-87 flex-wrap items-center gap-1">
      {options.map(option => (
        <Fragment key={String(option.value)}>
          <button
            data-testid={dataTestId && `${dataTestId}-option-${(option.value ?? "all").replaceAll("_", "-")}`}
            onClick={() => onSelect(option.value)}
            disabled={selected === option.value}
            className={selected === option.value ? OPTION_CHIP_CLASS.selected : OPTION_CHIP_CLASS.unselected}>
            {option.label}
          </button>
          {option.value === null && <div className="w-full" />}
        </Fragment>
      ))}
    </div>
  );

  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="focus-within:border-primary-dark-solid border-neutral-grey-light-dark flex h-10 w-fit items-center justify-between gap-2.5 rounded-sm border px-5 py-3 transition-all duration-150 sm:py-0">
        <BuildingIcon className="text-neutral-grey-dark" />
        <div className="bg-neutral-white-dark h-5 w-px" />
        <input
          placeholder={t("search_placeholder")}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          className="w-full text-sm leading-0 font-semibold outline-hidden sm:w-64"
        />
      </div>
      <FilterButton
        value={search.package_name ? planLabel(search.package_name) : t("common:all")}
        clear={() => update({package_name: undefined})}
        title={t("filter_plan")}
        subTitle={t("filter_plan")}
        isDeleteIconActive={!!search.package_name}>
        {renderOptions<(typeof PLAN_TIERS)[number]>(
          [{label: t("common:all"), value: null}, ...PLAN_TIERS.map(tier => ({label: planLabel(tier), value: tier}))],
          search.package_name ?? null,
          value => update({package_name: value ?? undefined}),
        )}
      </FilterButton>
      <FilterButton
        value={billing ? billingLabels[billing] : t("common:all")}
        clear={() => setBilling(null)}
        title={t("filter_billing")}
        subTitle={t("filter_billing")}
        isDeleteIconActive={billing !== null}>
        {renderOptions<TBillingOption>(
          [
            {label: t("common:all"), value: null},
            ...BILLING_PERIODS.map(period => ({label: billingLabels[period], value: period as TBillingOption})),
            {label: billingLabels.three_plus_twelve, value: "three_plus_twelve"},
          ],
          billing,
          setBilling,
        )}
      </FilterButton>
      <FilterButton
        value={search.billing_source ? billingSourceLabels[search.billing_source] : t("common:all")}
        dataTestId="overview-filter-source"
        clear={() => update({billing_source: undefined})}
        title={t("filter_source")}
        subTitle={t("filter_source")}
        isDeleteIconActive={!!search.billing_source}>
        {renderOptions<(typeof BILLING_SOURCES)[number]>(
          [
            {label: t("common:all"), value: null},
            ...BILLING_SOURCES.map(source => ({label: billingSourceLabels[source], value: source})),
          ],
          search.billing_source ?? null,
          value => update({billing_source: value ?? undefined}),
          "overview-filter-source",
        )}
      </FilterButton>
      <FilterButton
        value={renewal ? renewalLabel(renewal) : t("common:all")}
        clear={() => setRenewal(null)}
        title={t("filter_renewal")}
        subTitle={t("filter_renewal")}
        isDeleteIconActive={renewal !== null}>
        {renderOptions<TRenewalOption>(
          [
            {label: t("common:all"), value: null},
            ...RENEWAL_OPTIONS.map(option => ({label: renewalLabel(option), value: option as TRenewalOption})),
            ...renewalMonths.map(month => ({label: renewalLabel(month), value: month})),
          ],
          renewal,
          setRenewal,
        )}
      </FilterButton>
      <SortButton
        ClosedIconComponent={<DownUpArrowIcon className="text-neutral-grey-dark size-4.5" />}
        OpenedIconComponent={<DownUpArrowIcon className="text-primary-dark-solid size-4.5" />}
        ordering={sortOption}
        setOrdering={(value: SetStateAction<TSortOption | null>) => {
          const option = typeof value === "function" ? value(sortOption) : value;
          update({
            ordering:
              (option?.replace(CPM_OPTION_REGEX, `$1${search.window}_$2`) as TCustomerSuccessOrdering) ?? undefined,
          });
        }}
        translations={sortLabels}
      />
      {hasActiveFilters && (
        <Button
          buttonStyle="White"
          className="h-10 px-2 text-sm sm:px-5"
          dataTestId="overview-reset-filters"
          onClick={() => {
            setSearchText("");
            navigate({to: ".", search: {window: search.window}, replace: true, resetScroll: false});
          }}>
          <span className="text-neutral-black">{t("common:reset")}</span>
        </Button>
      )}
      <SegmentedToggle
        className="w-fit grow-0 sm:ml-auto"
        options={[
          {label: t("window_lifetime"), onClick: () => setWindow("lifetime")},
          {label: t("window_since_renewal"), onClick: () => setWindow("period")},
        ]}
        selectedIndex={search.window === "period" ? 1 : 0}
        variant="small"
      />
      {children}
    </div>
  );
};

export default CustomerSuccessFilters;
